import { zodResolver } from '@hookform/resolvers/zod';
import { T, useT } from '@transifex/react';
import { Text, VStack } from '@vidahealth/vida-ui';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { View } from 'react-native';

import {
  US_STATES,
  useAddressVerification,
  VerificationDialogs,
} from '@/modules/addressVerification';
import { logError, trackEvent } from '@/modules/analytics';
import { useRulesEngine } from '@/modules/rulesEngine';
import {
  BottomSheet,
  CtaButton,
  LoadingScreen,
  PhoneField,
  Screen,
  ScreenHeading,
  SelectField,
  TextField,
  formatZipCode,
  type SelectOption,
} from '@/modules/ui';

import { memberContactSchema, type MemberContactFormValues } from './schema/memberContactSchema';
import { mapUserToFormValues } from './utils/mapUserToFormValues';
import { submitMemberContact } from './utils/submitMemberContact';

const US_STATE_OPTIONS: SelectOption[] = US_STATES.map((s) => ({
  value: s.code,
  label: `${s.code} – ${s.name}`,
}));

const ZIP_MAX_LENGTH = 5;

/**
 * MEMBER CONTACT (ME-1256) — collects address + phone after the coach phase,
 * gated by the member-contact experiment. All fields are required, but the
 * screen itself is optional via "Skip for now" (skipping leaves the MWL
 * checklist item incomplete). Saving submits a Member Contact program-unit
 * instance, which writes to the profile + checks off the checklist backend-side.
 *
 * Continue validates the form, runs Lob address verification (ME-1581) — which
 * may surface a confirm/warning/undeliverable/error sheet — then submits the
 * accepted address and advances.
 */
export function MemberContactScreen() {
  const t = useT();
  const { user, refreshUser, setCurrentRoute, dismissMemberContact } = useRulesEngine();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(false);
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  // True until the on-mount user refresh settles — gates the form render so the
  // fields don't pop in with stale (pre-refresh) values.
  const [isPrefilling, setIsPrefilling] = useState(true);

  const {
    control,
    // Read isDirty during render so react-hook-form subscribes to it — otherwise
    // it stays false and the Skip-confirm dirty check never fires.
    formState: { isDirty },
    getValues,
    reset,
    trigger,
  } = useForm<MemberContactFormValues>({
    resolver: zodResolver(memberContactSchema),
    mode: 'onBlur',
    reValidateMode: 'onChange',
    defaultValues: mapUserToFormValues(user),
  });

  // Lob address verification (ME-1581). verify() runs before the save and drives
  // its own dialogs; isVerifying covers the network call (dialogs are their own UI).
  const { verify, dialogState, isVerifying } = useAddressVerification({ surface: 'onboarding' });

  // Leaving onboarding (Continue after a successful save, or Skip). The coach is
  // already assigned, so the next stop is the terminal transition screen.
  const advanceToTerminal = () => {
    dismissMemberContact();
    setCurrentRoute('/step/transition-to-native-app');
  };

  // Continue validates, verifies the address with Lob (which drives its own
  // dialogs), then submits. Uses trigger()/getValues() rather than handleSubmit so
  // the verify + corrected-address write-back are plain linear async we control —
  // in particular reset() stays out of RHF's submit callback, where resetting mid-
  // submit fights the form's submit lifecycle (RHF's guidance is to reset elsewhere).
  const onContinue = async () => {
    trackEvent('member-contact-click-continue', { uuid: user?.uuid });

    const isValid = await trigger();
    if (!isValid) {
      // trigger() has surfaced the inline field errors; record the blocked attempt
      // so "tried to save but had errors" shows up in the funnel.
      trackEvent('member-contact-continue-validation-error', { uuid: user?.uuid });
      return;
    }

    setSubmitError(false);
    if (!user?.uuid) {
      // The driver only routes here for an authenticated user, so this is an
      // invariant violation, not a save failure — log it and bail rather than
      // show a misleading "something went wrong saving" error.
      void logError('MemberContactScreen: submit reached without an authenticated user uuid');
      return;
    }

    const values = getValues();
    try {
      // Verify the address with Lob before saving. verify() drives its own dialogs
      // and resolves only once the member decides: `!ok` means they backed out
      // (stay on the form); `ok` carries the address to save — Lob-corrected if
      // they accepted a suggestion.
      // `values` carries an extra `phone`, but verify() runs `trimAddress` first,
      // which projects to exactly the five address fields — so phone never reaches
      // Lob or the returned address. No need to hand-pick the address fields here.
      const result = await verify(values);
      if (!result.ok) return;

      // Overlay the accepted address onto the form values (keeps phone). `Address`
      // is exactly the five address fields, so the spread overrides only those.
      // addressLine2 needs the explicit `?? ''`: when the accepted address has no
      // line 2 (e.g. Lob dropped an unnecessary unit) it comes through as
      // `undefined`, and resetting a field to `undefined` leaves the member's
      // previously-typed value in place — the empty string forces it to clear.
      const acceptedValues: MemberContactFormValues = {
        ...values,
        ...result.address,
        addressLine2: result.address.addressLine2 ?? '',
      };
      // Only when the member accepted a Lob suggestion (`changed`) do we write the
      // corrected values back — so a failed save leaves them on screen. An
      // unchanged result would just be a no-op re-render, so skip it.
      if (result.changed) reset(acceptedValues, { keepDefaultValues: true });

      setIsSubmitting(true);
      await submitMemberContact(user.uuid, acceptedValues);
      // Pull the just-saved address/phone back onto the user so the completeness
      // gate is satisfied on any subsequent resolve. Best-effort — advancing
      // doesn't depend on it.
      try {
        await refreshUser();
      } catch {
        // Ignore — the submit already succeeded; the terminal is the safe next stop.
      }
      advanceToTerminal();
    } catch {
      setSubmitError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Skip is always allowed, but if the member has started entering info confirm
  // first so a stray tap doesn't discard their input.
  const onSkipPress = () => {
    if (isDirty) {
      setSkipConfirmOpen(true);
      return;
    }
    confirmSkip();
  };

  const confirmSkip = () => {
    setSkipConfirmOpen(false);
    trackEvent('member-contact-click-skip', { uuid: user?.uuid });
    advanceToTerminal();
  };

  // Refresh the persisted user on mount, then seed the form from the freshly
  // fetched user so the prefill reflects the latest saved data (phone from signup,
  // any address/state/ZIP already on file). Best-effort: on failure we keep
  // whatever the driver last loaded.
  useEffect(() => {
    let cancelled = false;
    void refreshUser()
      .then((freshUser) => {
        if (!cancelled) reset(mapUserToFormValues(freshUser));
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsPrefilling(false);
      });
    return () => {
      cancelled = true;
    };
  }, [refreshUser, reset]);

  // Busy while saving or while the Lob check is in flight — disables the CTAs.
  const busy = isSubmitting || isVerifying;

  // Hold until the on-mount refresh seeds the form, so fields don't pop in.
  if (isPrefilling) return <LoadingScreen />;

  return (
    <Screen
      busy={busy}
      footer={
        <VStack space='md'>
          <CtaButton
            isBusy={busy}
            isDisabled={busy}
            onPress={onContinue}
            testID='member-contact-continue'
          >
            <T _str='Continue' />
          </CtaButton>
          <CtaButton
            type='secondary'
            isDisabled={busy}
            onPress={onSkipPress}
            testID='member-contact-skip'
          >
            <T _str='Skip for now' />
          </CtaButton>
        </VStack>
      }
    >
      <VStack space='2xl'>
        <ScreenHeading
          title={t('How can we reach you?')}
          subtitle={t(
            "Your care team needs this to deliver virtual care and get you help fast if there's ever an emergency."
          )}
        />

        {submitError ? (
          <Text size='sm' className='text-error-700'>
            <T _str='Something went wrong saving your contact info. Please try again.' />
          </Text>
        ) : null}

        <PhoneField
          control={control}
          name='phone'
          placeholder={t('Phone number')}
          testID='member-contact-phone'
        />

        <TextField
          control={control}
          name='addressLine1'
          placeholder={t('Street address')}
          testID='member-contact-address-line1'
        />

        <TextField
          control={control}
          name='addressLine2'
          placeholder={t('Apt / Suite / Unit (optional)')}
          testID='member-contact-address-line2'
        />

        <TextField
          control={control}
          name='city'
          placeholder={t('City')}
          testID='member-contact-city'
        />

        <View className='w-full flex-col gap-6 sm:flex-row'>
          <View className='flex-1'>
            <SelectField
              control={control}
              name='state'
              options={US_STATE_OPTIONS}
              placeholder={t('State')}
              testID='member-contact-state'
            />
          </View>
          <View className='flex-1'>
            <TextField
              control={control}
              name='zip'
              placeholder={t('ZIP code')}
              format={formatZipCode}
              inputMode='numeric'
              keyboardType='number-pad'
              maxLength={ZIP_MAX_LENGTH}
              testID='member-contact-zip'
            />
          </View>
        </View>
      </VStack>

      <BottomSheet
        isOpen={skipConfirmOpen}
        onClose={() => setSkipConfirmOpen(false)}
        footer={
          <VStack space='md'>
            <CtaButton onPress={confirmSkip} testID='member-contact-skip-confirm'>
              <T _str='Skip anyway' />
            </CtaButton>
            <CtaButton
              type='secondary'
              onPress={() => setSkipConfirmOpen(false)}
              testID='member-contact-skip-cancel'
            >
              <T _str='Keep editing' />
            </CtaButton>
          </VStack>
        }
      >
        <VStack space='md'>
          <ScreenHeading title={t('Skip contact info?')} />
          <Text size='md' className='text-typography-700'>
            <T _str="You've started entering your info. If you skip now, it won't be saved and your care team may not be able to reach you." />
          </Text>
        </VStack>
      </BottomSheet>

      <VerificationDialogs state={dialogState} surface='onboarding' />
    </Screen>
  );
}
